import type { PageContext } from "vike/types";

import { currentIcp } from "@/5-shared/routing/icp";

import { normalizeCampaign } from "./campaign";
import { AnalyticsEvent, type AnalyticsEventName } from "./events";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || "G-6SLB5TZS00";
// Only the live site reports, through its first-party /gtm proxy. Every other
// host (dev servers, the testing deploy, LAN previews, tunnels) used to share
// this property and made up 40% of its sessions. Debug on production with
// Tag Assistant instead.
const GA_HOST = "dec-energy.ch";

let initialized = false;
let lastPagePath: string | null = null;

// Must use `arguments` (not rest params) so gtag.js recognises the entries
 
function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export function initGa4() {
  if (initialized || typeof window === "undefined" || window.location.hostname !== GA_HOST) return;
  initialized = true;

  const s = document.createElement("script");
  s.async = true;
  s.src = `/gtm/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  gtag("js", new Date());
  gtag("config", GA_ID, {
    send_page_view: false,
    transport_url: `${window.location.origin}/gtm`,
  });
}

export function resetPageViewDedup() {
  lastPagePath = null;
}

// Events always fire. GA4 Consent Mode (default in +Head.tsx) redacts them to
// cookieless pings while `analytics_storage` is denied, so refusal still yields
// cookieless measurement rather than nothing. `icp` is a non-unique category
// read live from the URL, attached as an event-scoped param (no persistence).
export function trackPageView(opts?: { dedupe?: boolean }) {
  if (typeof window === "undefined") return;

  // Normalize away a trailing slash (except root) so SPA/inbound `/it/` does not
  // split from `/it` in GA4. nginx 301-strips on hard hits, but client-routing
  // records `page_path` verbatim, so the fix has to live here too.
  const pathname = window.location.pathname;
  const cleanPath =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  // Legacy hand-built UTMs are mapped onto GA4's channels (see campaign.ts).
  const path = cleanPath + normalizeCampaign(window.location.search);

  if (opts?.dedupe !== false) {
    if (lastPagePath === path) return;
    lastPagePath = path;
  }

  const icp = currentIcp();

  gtag("event", "page_view", {
    page_location: window.location.origin + path + window.location.hash,
    page_path: path,
    page_title: document.title,
    ...(icp ? { icp } : {}),
  });
}

export function trackEvent(
  name: AnalyticsEventName,
  params: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") return;

  const icp = currentIcp();

  gtag("event", name, {
    ...params,
    ...(icp ? { icp } : {}),
  });
}

export function setAnalyticsConsent(granted: boolean) {
  gtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
}

export function CTAbuttonTracking(href: string | undefined, pc: PageContext) {
  if (!href) return;
  const destination = format(
    href.split("?")[0]?.split("#")[0]?.split("/").pop() || href,
  );
  // Params are named `cta_*`: GA4 treats bare `source`/`medium`/`campaign` event
  // params as manual traffic-source overrides, which was rewriting the session
  // source to the click's originating path.
  trackEvent(AnalyticsEvent.CtaClick, {
    cta_destination: destination,
    cta_location: pc.urlPathname,
  });
}

function format(str: string) {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}
